import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MediaObserver } from '@angular/flex-layout';
import { Observable, Subject } from 'rxjs';

import { CheckboxDialogComponent, BaseCheckboxDialogOptions } from './checkbox/checkbox.dialogcomponent';
import { CountdownDialogComponent, BaseCountdownDialogOptions } from './countdown/countdown.dialogcomponent';
import { ConfirmationDialogComponent, BaseConfirmationDialogOptions } from './confirmation/confirmation.dialogcomponent';
import { InputBoxDialogComponent, BaseInputBoxDialogOptions } from './inputbox/inputbox.dialogcomponent';
import { LoginBoxDialogComponent, BaseLoginBoxDialogOptions, BaseLoginBoxDialogResult } from './loginbox/loginbox.dialogcomponent';
import { MessageBoxDialogComponent, BaseMessageBoxDialogOptions } from './messagebox/messagebox.dialogcomponent';
import { OptionBoxDialogComponent, BaseOptionBoxDialogOptions, BaseOptionBoxOption } from './optionbox/optionbox.dialogcomponent';
import { NomineeSearchDialogComponent, BaseNomineeSearchDialogOptions } from './nomineesearch/nomineesearch.dialogcomponent';
import { NomineeInputDialogComponent } from './nomineeinput/nomineeinput.dialogcomponent';
import { ReportOptionsDialogComponent, BaseReportOptionsDialogOptions, BaseReportOptionsResult } from './reportoptions/reportoptions.dialogcomponent';
import { SelectBoxDialogComponent, BaseSelectBoxDialogOptions, BaseSelectBoxSelectionOption } from './selectbox/selectbox.dialogcomponent';
import { TextNoteDialogComponent, BaseTextNoteDialogOptions } from './textnote/textnote.dialogcomponent';
import { UserSearchDialogComponent, BaseUserSearchDialogOptions } from './usersearch/usersearch.dialogcomponent';
import { UserSearchResult, NomineeInfo, NomineeTypeInfo, JudgeInfo, TieBreakerWindow, JudgeScoresInfo } from 'services/application/application.service';
import { JudgeAddDialogComponent } from './judgeadd/judgeadd.dialogcomponent';
import { JudgeEmailDialogComponent } from './judgeemail/judgeemail.dialogcomponent';
import { JudgeSpreadsheetDialogComponent } from './judgespreadsheet/judgespreadsheet.dialogcomponent';
import { JudgeConfirmDialogComponent } from './judgeconfirm/judgeconfirm.dialogcomponent';
import { TieBreakerDialogComponent, BaseTieBreakerDialogOptions } from './tiebreaker/tiebreaker.dialogcomponent';
import { TieOverrideDialogComponent, BaseTieOverrideDialogOptions } from './tieoverride/tieoverride.dialogcomponent';
import { JudgeDivideDialogComponent } from './judgedivide/judgedivide.dialogcomponent';

export { BaseCountdownDialogOptions as CountdownDialogOptions };
export { BaseConfirmationDialogOptions as ConfirmationDialogOptions };
export { BaseInputBoxDialogOptions as InputBoxDialogOptions };
export { BaseLoginBoxDialogOptions as LoginBoxDialogOptions };
export { BaseLoginBoxDialogResult as LoginBoxDialogResult };
export { BaseMessageBoxDialogOptions as MessageBoxDialogOptions };
export { BaseNomineeSearchDialogOptions as NomineeSearchDialogOptions };
export { BaseOptionBoxDialogOptions as OptionBoxDialogOptions };
export { BaseOptionBoxOption as OptionBoxOption };
export { BaseReportOptionsDialogOptions as ReportOptionsDialogOptions };
export { BaseReportOptionsResult as ReportOptionsResult };
export { BaseSelectBoxDialogOptions as SelectBoxDialogOptions };
export { BaseSelectBoxSelectionOption as SelectBoxSelectionOption };
export { BaseTextNoteDialogOptions as TextNoteDialogOptions };
export { BaseUserSearchDialogOptions as UserSearchDialogOptions };

@Injectable({
    providedIn: 'root'
})

export class DialogService {
    smallMediaSize: boolean;

    constructor(private matDialog: MatDialog, private mediaChange: MediaObserver) {
        this.smallMediaSize = (mediaChange.isActive('screen and (max-width: 959px)'));

        // Observe for media changes
        this.mediaChange.media$.subscribe(media => {
            // Set flags accordingly with what we know.
            this.smallMediaSize = (media.mqAlias === 'xs' || media.mqAlias === 'sm');
        });
    }

    // #region Close All Dialogs Service Call

    CloseAllDialogs() {
        this.matDialog.closeAll();
    }

    // #endregion

    // #region Open Checkbox Dialog Service Call

    OpenCheckboxDialog(options?: BaseCheckboxDialogOptions): Observable<boolean> {
        const openHandle = this.matDialog.open<CheckboxDialogComponent>(CheckboxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information Requested',
                MessageText: '',
                OKText: 'OK',
                ...options
            },
            width: options.width || '800px',
            height: options.height || '400px',
            maxHeight: '85vh'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Confirmation Dialog Service Call

    OpenConfirmationDialog(options?: BaseConfirmationDialogOptions): Observable<boolean> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Confirmation Requested...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options
            },
            panelClass: 'confdialog',
            maxHeight: '85vh'// ,
            // width: '80vw'
            // ,
            // height: (self.smallMediaSize ? '80vh' : '600px')
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = ['confdialog', 'smalldialogpanel'];
        }

        const openHandle = this.matDialog.open<ConfirmationDialogComponent>(ConfirmationDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Countdown Dialog Service Call

    OpenCountdownDialog(options?: BaseCountdownDialogOptions): Observable<boolean> {
        const self = this;

        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Please Wait...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                Time: 60,
                ...options
            },
            width: '500px',
            height: '18rem',
            maxHeight: '85vh'
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            // dlgoptions.height = '80vh';
            dlgoptions.height = '30rem';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<CountdownDialogComponent>(CountdownDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Input Box Dialog Service Call

    OpenInputBoxDialog(options?: BaseInputBoxDialogOptions): Observable<string> {
        const openHandle = this.matDialog.open<InputBoxDialogComponent>(InputBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information Requested...',
                MessageText: '',
                InputHint: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options
            },
            maxHeight: '85vh'
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Judge Add Dialog Service Call

    OpenJudgeAddDialog(nomTypes?: NomineeTypeInfo[], judge?: JudgeInfo): Observable<JudgeInfo> {
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                nomTypes: nomTypes,
                judge: judge
            },
            width: '600px'
        };

        const openHandle = this.matDialog.open<JudgeAddDialogComponent>(JudgeAddDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Judge Confirm Dialog Service Call

    OpenJudgeConfirmDialog(judges?: JudgeInfo[]): Observable<JudgeInfo[]> {
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                judges: judges
            },
            width: '1500px'
        };

        const openHandle = this.matDialog.open<JudgeConfirmDialogComponent>(JudgeConfirmDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Judge Edit Dialog Service Call

    OpenJudgeEmailDialog(nomTypes?: NomineeTypeInfo[]): Observable<JudgeInfo> {
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                nomTypes: nomTypes
            },
            width: '500px'
        };

        const openHandle = this.matDialog.open<JudgeEmailDialogComponent>(JudgeEmailDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Judge Spreadhseet Dialog Service Call

    OpenJudgeSpreadsheetDialog(): Observable<File> {
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            width: '700px'
        };

        const openHandle = this.matDialog.open<JudgeSpreadsheetDialogComponent>(JudgeSpreadsheetDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Login Box Dialog Service Call

    OpenLoginBoxDialog(options?: BaseLoginBoxDialogOptions): Observable<BaseLoginBoxDialogResult> {
        const openHandle = this.matDialog.open<LoginBoxDialogComponent>(LoginBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Credentials Required...',
                MessageText: '',
                InputHint: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options
            },
            maxHeight: '85vh'
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Message Box Dialog Service Call

    OpenMessageBoxDialog(options?: BaseMessageBoxDialogOptions): Observable<boolean> {
        const self = this;

        const openHandle = self.matDialog.open<MessageBoxDialogComponent>(MessageBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information',
                MessageText: '',
                OKText: 'OK',
                ...options
            },
            width: (self.smallMediaSize ? '80vw' : (options.width || '600px')),
            height: (self.smallMediaSize ? '80vh' : (options.height || '300px')),
            maxHeight: '85vh'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Nominee Search Dialog Service Call

    OpenNomineeSearchDialog(options?: BaseNomineeSearchDialogOptions): Observable<NomineeInfo> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'User Search',
                MessageText: '',
                OKText: 'Select',
                CancelText: 'Cancel',
                ...options
            },
            maxHeight: '85vh'// ,
            // width: '80vw'
            // ,
            // height: (self.smallMediaSize ? '80vh' : '600px')
        };

        if (options.width) { dlgoptions.width = options.width; }

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<NomineeSearchDialogComponent>(NomineeSearchDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Nominee Input Dialog Service Call

    OpenNomineeInputDialog(options?: BaseDialogOptions): Observable<NomineeInfo> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: options.TitleText,
                MessageText: options.MessageText,
                OKText: options.OKText,
                CancelText: options.CancelText
            },
            maxHeight: '85vh'
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<NomineeInputDialogComponent>(NomineeInputDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Option Box Dialog Service Call

    OpenOptionBoxDialog(options?: BaseOptionBoxDialogOptions): Observable<any> {
        const openHandle = this.matDialog.open<OptionBoxDialogComponent>(OptionBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Please select',
                MessageText: '',
                Options: [],
                ...options
            },
            maxHeight: '85vh'
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Report Options Dialog Service Call

    OpenReportOptionsDialog(options?: BaseReportOptionsDialogOptions): Observable<BaseReportOptionsResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Choose Report Options...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options
            },
            maxHeight: '85vh'// ,
            // width: '80vw'
            // ,
            // height: (self.smallMediaSize ? '80vh' : '600px')
        };

        if (options.width) { dlgoptions.width = options.width; }

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            // dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<ReportOptionsDialogComponent>(ReportOptionsDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Select Box Dialog Service Call

    OpenSelectBoxDialog(options?: BaseSelectBoxDialogOptions): Observable<any> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Make Choice...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options
            },
            maxHeight: '85vh'// ,
            // width: '80vw'
            // ,
            // height: (self.smallMediaSize ? '80vh' : '600px')
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<SelectBoxDialogComponent>(SelectBoxDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Text Note Dialog Service Call

    OpenTextNoteDialog(options?: BaseTextNoteDialogOptions): Observable<string> {
        const openHandle = this.matDialog.open<TextNoteDialogComponent>(TextNoteDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Optional Note',
                MessageText: '',
                OKText: 'OK',
                ...options
            },
            maxHeight: '85vh'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Tie Breaker Dialog Service Call

    OpenTieBreakerDialog(options: BaseTieBreakerDialogOptions): Observable<TieBreakerWindow> {
        const openHandle = this.matDialog.open<TieBreakerDialogComponent>(TieBreakerDialogComponent, {
            disableClose: true,
            data: {
                Options: options
            },
            maxHeight: '85vh'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Tie Override Dialog Service Call

    OpenTieOverrideDialog(options: BaseTieOverrideDialogOptions): Observable<JudgeScoresInfo[]> {
        const openHandle = this.matDialog.open<TieOverrideDialogComponent>(TieOverrideDialogComponent, {
            disableClose: true,
            data: {
                Options: options
            },
            maxHeight: '85vh',
            width: '1000px'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open User Search Dialog Service Call

    OpenUserSearchDialog(options?: BaseUserSearchDialogOptions): Observable<UserSearchResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'User Search',
                MessageText: '',
                OKText: 'Select',
                CancelText: 'Cancel',
                ...options
            },
            maxHeight: '85vh'// ,
            // width: '80vw'
            // ,
            // height: (self.smallMediaSize ? '80vh' : '600px')
        };

        if (options.width) { dlgoptions.width = options.width; }

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<UserSearchDialogComponent>(UserSearchDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Judge Divide Dialog Service Call

    OpenJudgeDivideDialog(jCount: number, nCount: number) {
        const dlgOptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                JudgeCount: jCount,
                NomineeCount: nCount
            }
        };

        const openHandle = this.matDialog.open<JudgeDivideDialogComponent>(JudgeDivideDialogComponent, dlgOptions);

        return openHandle.afterClosed();
    }

    // #endregion
}

// #region Custom Classes

    // #region Base Dialog Options Class

    export class BaseDialogOptions {
        TitleText?: string;
        MessageText?: string;
        OKText?: string;
        CancelText?: string;
        IconColor?: string;
        IconName?: string;
        ShowIcon?: boolean;
        height?: string;
        width?: string;
    }

    // #endregion

// #endregion
